import {
  useCancelMediaUpload,
  useCompleteMediaUpload,
  useCreateMediaUpload,
} from '@reelplicate/api-client/media';
import type { MediaAssetDto, MediaContentType } from '@reelplicate/api-client/model';
import { useCallback } from 'react';

import { compressPhoto, putToSignedUrl, putWithProgress } from '@/lib/upload';

import { probeMedia } from './probe';

export type MediaUploadOptions = {
  signal?: AbortSignal;
  /** 0–100 while the file goes to storage. */
  onProgress?: (percent: number) => void;
  /** The file is in storage; the library is taking it in. */
  onStored?: () => void;
  /** A hand-picked collection the file also goes into. */
  collectionId?: string;
};

export const isAbort = (error: unknown) =>
  error instanceof DOMException && error.name === 'AbortError';

/**
 * Adds a file to the media library: start → PUT to storage (with progress)
 * → poster frame → complete, resolving to the new asset. On failure or
 * abort the half-made asset is cancelled and the error rethrown. Used by the
 * library's upload queue and by Create post.
 */
export function useMediaUpload() {
  const createUpload = useCreateMediaUpload();
  const completeUpload = useCompleteMediaUpload();
  const cancelUpload = useCancelMediaUpload({ mutation: { meta: { silent: true } } });
  const { mutateAsync: create } = createUpload;
  const { mutateAsync: complete } = completeUpload;
  const { mutate: cancel } = cancelUpload;

  return useCallback(
    async (
      workspaceId: string,
      picked: File,
      { signal, onProgress, onStored, collectionId }: MediaUploadOptions = {},
    ): Promise<MediaAssetDto> => {
      const file = await compressPhoto(picked);
      const probe = await probeMedia(file);
      const started = await create({
        id: workspaceId,
        data: {
          fileName: file.name,
          contentType: file.type as MediaContentType,
          size: file.size,
          collectionId,
        },
      });
      try {
        await putWithProgress(started.file.url, file, { signal, onProgress });
        onStored?.();
        let thumbnail = false;
        if (started.thumbnail && probe.poster) {
          thumbnail = await putToSignedUrl(started.thumbnail.url, probe.poster).then(
            () => true,
            () => false,
          );
        }
        return await complete({
          id: workspaceId,
          assetId: started.asset.id,
          data: {
            durationSeconds: probe.durationSeconds,
            width: probe.width,
            height: probe.height,
            thumbnail,
          },
        });
      } catch (error) {
        cancel({ id: workspaceId, assetId: started.asset.id });
        throw error;
      }
    },
    [create, complete, cancel],
  );
}
